import { Moment } from 'moment'
import React, { useState } from 'react'
import { MIN_YEAR, RU_LOCALE } from '../../constants/constants'
import { CalendarMode, CalendarProps } from '../../types'
import { generateCalendarDays } from '../../utils/dateCalculations'
import {
	isDateInRange,
	isRangeBoundary,
	isToday,
} from '../../utils/dateValidation'
import CalendarHeader from '../CalendarHeader'
import DayCell from '../DayCell'
import MonthPicker from '../MonthPicker'
import YearPicker from '../YearPicker'
import {
	CalendarContent,
	DaysGrid,
	StyledCalendar,
	WeekdayLabel,
	WeekdaysRow,
} from './Calendar.styles'

/**
 * Основной компонент календаря
 * Объединяет все подкомпоненты и управляет состоянием отображения
 * Поддерживает три режима: days (выбор дня), months (выбор месяца), years (выбор года)
 */
const Calendar: React.FC<CalendarProps> = ({
	currentDate,
	selectedDate,
	selectedRange,
	mode: selectionMode,
	onSelectDate,
	onSelectRange,
	isDateDisabled,
	onMonthChange,
}) => {
	const [calendarMode, setCalendarMode] = useState<CalendarMode>('days')
	const [displayDate, setDisplayDate] = useState<Moment>(currentDate.clone())
	const [hoverDate, setHoverDate] = useState<Moment | null>(null)

	// Генерируем дни для отображения
	const calendarDays = generateCalendarDays(displayDate)

	const handlePrevious = () => {
		if (calendarMode === 'days') {
			const newDate = displayDate.clone().subtract(1, 'month')
			const minMonth = displayDate
				.clone()
				.set({ year: MIN_YEAR, month: 0 })
				.startOf('month')
			if (newDate.clone().startOf('month').isBefore(minMonth)) return
			setDisplayDate(newDate)
			onMonthChange?.(newDate)
		} else if (calendarMode === 'months' || calendarMode === 'years') {
			const newDate = displayDate.clone().subtract(1, 'year')
			if (newDate.year() < MIN_YEAR) return
			setDisplayDate(newDate)
		}
	}

	const handleNext = () => {
		if (calendarMode === 'days') {
			const newDate = displayDate.clone().add(1, 'month')
			setDisplayDate(newDate)
			onMonthChange?.(newDate)
		} else if (calendarMode === 'months' || calendarMode === 'years') {
			const newDate = displayDate.clone().add(1, 'year')
			setDisplayDate(newDate)
		}
	}

	const handleModeChange = (mode: CalendarMode) => {
		setCalendarMode(mode)
	}

	const handleSelectMonth = (month: number) => {
		const newDate = displayDate.clone().month(month)
		setDisplayDate(newDate)
		setCalendarMode('days')
		onMonthChange?.(newDate)
	}

	const handleSelectYear = (year: number) => {
		const newDate = displayDate.clone().year(year)
		setDisplayDate(newDate)
		setCalendarMode('months')
	}

	const handleBackFromMonthPicker = () => {
		setCalendarMode('days')
	}

	const handleBackFromYearPicker = () => {
		setCalendarMode('months')
	}

	const handleDayClick = (date: Moment) => {
		if (selectionMode === 'single') {
			onSelectDate?.(date)
		} else if (selectionMode === 'range') {
			if (
				!selectedRange ||
				selectedRange[0] === null ||
				selectedRange[1] !== null
			) {
				// Начинаем новый выбор диапазона
				onSelectRange?.([date, null])
			} else {
				// Завершаем выбор диапазона
				const [start] = selectedRange
				if (date.isBefore(start)) {
					onSelectRange?.([date, start])
				} else {
					onSelectRange?.([start, date])
				}
			}
		}
	}

	const renderDaysView = () => {
		return (
			<>
				<WeekdaysRow>
					{RU_LOCALE.WEEKDAYS_SHORT.map(day => (
						<WeekdayLabel key={day}>{day}</WeekdayLabel>
					))}
				</WeekdaysRow>
				<DaysGrid>
					{calendarDays.map((date, index) => {
						const isCurrentMonth = date.month() === displayDate.month()
						const isTodayDate = isToday(date)
						const isDisabled = isDateDisabled ? isDateDisabled(date) : false

						let isSelected = false
						let isInRange = false
						let isRangeStart = false
						let isRangeEnd = false

						if (selectionMode === 'single' && selectedDate) {
							isSelected = date.isSame(selectedDate, 'day')
						} else if (selectionMode === 'range' && selectedRange) {
							const [start, end] = selectedRange
							if (start && end) {
								isInRange = isDateInRange(date, start, end)
								const boundaries = isRangeBoundary(date, start, end)
								isRangeStart = boundaries.isStart
								isRangeEnd = boundaries.isEnd
							} else if (start && hoverDate) {
								// Показываем preview диапазона при наведении
								const previewStart = start.isBefore(hoverDate)
									? start
									: hoverDate
								const previewEnd = start.isBefore(hoverDate) ? hoverDate : start
								isInRange = isDateInRange(date, previewStart, previewEnd)
								const boundaries = isRangeBoundary(
									date,
									previewStart,
									previewEnd,
								)
								isRangeStart = boundaries.isStart
								isRangeEnd = boundaries.isEnd
							} else if (start) {
								isSelected = date.isSame(start, 'day')
							}
						}

						return (
							<DayCell
								key={index}
								date={date}
								isSelected={isSelected}
								isToday={isTodayDate}
								isCurrentMonth={isCurrentMonth}
								isDisabled={isDisabled}
								isInRange={isInRange}
								isRangeStart={isRangeStart}
								isRangeEnd={isRangeEnd}
								onClick={handleDayClick}
								onMouseEnter={() => setHoverDate(date)}
								onMouseLeave={() => setHoverDate(null)}
							/>
						)
					})}
				</DaysGrid>
			</>
		)
	}

	return (
		<StyledCalendar>
			{calendarMode === 'days' && (
				<CalendarHeader
					currentDate={displayDate}
					mode={calendarMode}
					onModeChange={handleModeChange}
					onPrevious={handlePrevious}
					onNext={handleNext}
				/>
			)}
			<CalendarContent>
				{calendarMode === 'days' && renderDaysView()}
				{calendarMode === 'months' && (
					<MonthPicker
						currentYear={displayDate.year()}
						selectedMonth={displayDate.month()}
						onSelectMonth={handleSelectMonth}
						onBack={handleBackFromMonthPicker}
					/>
				)}
				{calendarMode === 'years' && (
					<YearPicker
						selectedYear={displayDate.year()}
						onSelectYear={handleSelectYear}
						onBack={handleBackFromYearPicker}
					/>
				)}
			</CalendarContent>
		</StyledCalendar>
	)
}

export default Calendar
